'use client'

import { useState, useEffect, useRef } from 'react'
import {
  Activity,
  X,
  ChevronRight,
  Loader2,
  Square,
  RotateCcw,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  ListTodo,
  BarChart3,
  Layers,
  Box,
  Terminal,
  GitBranch,
  Image,
  Cpu,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTaskSystem } from '@/lib/hooks/use-task-system'
import { commandApi } from '@/lib/api/command'
import { pipelineApi } from '@/lib/api/pipeline'
import { creationApi } from '@/lib/api/creation'
import { useFakeProgress } from '@/lib/hooks/use-fake-progress'
import type { PoolInfo, MonitorPipelineDetail, MonitorPipelineChildTask } from '@/lib/api/task-monitor'
import { taskMonitorApi } from '@/lib/api/task-monitor'
import type { PipelineProgressStage } from '@/lib/types/pipeline'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { useI18n } from '@/components/providers/I18nProvider'
import { cdnImg, zipImg } from '@/components/materials/utils'

const ACTIVE_PIPELINE_STATUSES = ['pending', 'running']
const ACTIVE_TASK_STATUSES = ['pending', 'queued', 'running']

/** Max list items rendered per tab before collapsing behind a "show all" toggle. */
const LIST_DISPLAY_LIMIT = 10

function pipelineTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    ecommerce_main_image: '电商主图',
    ecommerce_detail_image: 'A+详情图',
    ecommerce_hit_replicate: '爆款复刻',
    ecommerce_clothing_set: '服装组图',
    ecommerce_model_wear: '模特穿戴',
    ecommerce_product_refine: '产品精修',
    ecommerce_video: '电商视频',
    video_dubbing: 'AI配音',
  }
  return labels[type] || type
}

function getStepLabel(step: string, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const known: Record<string, string> = {
    analyze_product: 'pipeline.step.analyze_product',
    vision: 'pipeline.step.vision',
    brief: 'pipeline.step.brief',
    planner: 'pipeline.step.planner',
    composer: 'pipeline.step.composer',
    validator: 'pipeline.step.validator',
    detail_composer: 'pipeline.step.detail_composer',
    video_composer: 'pipeline.step.video_composer',
    generate_images: 'pipeline.step.generate_images',
    generate_video: 'pipeline.step.generate_video',
    collect_results: 'pipeline.step.collect_results',
    start: 'pipeline.step.start',
  }
  const key = known[step]
  if (key) return t(key)
  if (step.startsWith('generate_')) {
    const n = step.replace('generate_', '').replace('_', ' ')
    return t('pipeline.step.generate', { n })
  }
  if (step === 'synth_segments') return '分段配音'
  if (step === 'merge') return '合并配音'
  if (step.startsWith('synth_')) {
    return `配音合成第 ${step.replace('synth_', '')} 段`
  }
  return step
}

// ── Status helpers ────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status) {
    case 'running': return 'text-blue-500'
    case 'pending': case 'queued': case 'new': return 'text-amber-500'
    case 'succeeded': case 'completed': return 'text-emerald-500'
    case 'failed': return 'text-red-500'
    case 'canceled': return 'text-gray-400'
    case 'partially_succeeded': return 'text-yellow-500'
    default: return 'text-muted-foreground'
  }
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'running': return 'default'
    case 'pending': case 'queued': case 'new': return 'secondary'
    case 'succeeded': case 'completed': return 'default'
    case 'failed': return 'destructive'
    case 'canceled': return 'outline'
    default: return 'outline'
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'running': return <Loader2 className="h-3 w-3 animate-spin" />
    case 'pending': case 'queued': case 'new': return <Clock className="h-3 w-3" />
    case 'succeeded': case 'completed': return <CheckCircle2 className="h-3 w-3" />
    case 'failed': return <AlertCircle className="h-3 w-3" />
    case 'canceled': return <X className="h-3 w-3" />
    default: return <Activity className="h-3 w-3" />
  }
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: '等待中', queued: '排队中', running: '运行中',
    succeeded: '已完成', failed: '失败', canceled: '已取消',
    completed: '已完成', new: '新的', partially_succeeded: '部分成功',
  }
  return labels[status] || status
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  if (diff < 60000) return '刚刚'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
  return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// ── Real-time elapsed time ────────────────────────────────────────────
// Called on every render. The parent component re-renders every 1s via
// the `tick` state, so elapsed time stays live without a dedicated hook.
function elapsedTimeLive(startedAt: string | null | undefined): string {
  if (!startedAt) return '—'
  const diff = Date.now() - new Date(startedAt).getTime()
  const mins = Math.floor(diff / 60000)
  const secs = Math.floor((diff % 60000) / 1000)
  if (mins > 0) return `${mins}分${secs}秒`
  return `${secs}秒`
}

/** Format duration between two ISO timestamps, or from start to now if end is omitted. */
function formatDuration(startedAt: string | null | undefined, endedAt?: string | null | undefined): string {
  if (!startedAt) return '—'
  const end = endedAt ? new Date(endedAt).getTime() : Date.now()
  const diff = end - new Date(startedAt).getTime()
  if (diff < 0) return '—'
  const mins = Math.floor(diff / 60000)
  const secs = Math.floor((diff % 60000) / 1000)
  if (mins > 0) return `${mins}分${secs}秒`
  return `${secs}秒`
}

/** Recursively walk a value and replace any base64 / data-url strings with a
 *  truncated display showing the head and character count. This keeps the
 *  rendered JSON readable while still conveying the presence and approximate
 *  size of binary data. */
function sanitizeForDisplay(value: unknown): unknown {
  if (typeof value === 'string') {
    const s = value as string
    // data:image base64 — show prefix + truncated body
    if (s.startsWith('data:image')) {
      const comma = s.indexOf(',')
      if (comma !== -1) {
        const head = s.slice(0, 40)
        const body = s.slice(comma + 1)
        return `${head}...(base64, ${body.length} chars)`
      }
      // No comma in data URL — unusual, fall through
    }
    // Plain base64 (long alphanumeric+/=)
    if (s.length > 200 && /^[A-Za-z0-9+/=]+$/.test(s.slice(0, 100))) {
      return `${s.slice(0, 20)}...(base64, ${s.length} chars)`
    }
    // Very long JSON-stringified arrays — likely image data
    if (s.length > 500 && s.startsWith('[', 0) && s.endsWith(']')) {
      return `${s.slice(0, 30)}...(array, ${s.length} chars)`
    }
    return s
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeForDisplay)
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) {
      result[k] = sanitizeForDisplay(v)
    }
    return result
  }
  return value
}

// ── Step helpers (for execution record accordion) ──

/** Get the description for a pipeline step. */
function getStepDescription(step: string): string {
  const descriptions: Record<string, string> = {
    analyze_product: '调用多模态模型分析产品图片，识别商品的品类、外观特征、可见细节，并生成「外观锁定描述」供后续步骤复用。',
    vision: '调用视觉模型对产品图片进行视觉分析，提取关键视觉要素，为后续步骤提供参考。',
    brief: '根据商品识别结果和用户需求，生成详细的生图方案，包括构图、光影、色彩、场景等完整描述。',
    detail_composer: '编写商品详情页文案，组织卖点、规格、参数等内容，生成适合展示的详情页结构。',
    video_composer: '编写视频脚本，规划镜头、旁白、转场等视频元素，生成完整的视频制作方案。',
    generate_images: '根据生图方案调用生图模型生成商品图片，包括主图、细节图等多张输出。',
    generate_video: '根据视频脚本调用视频生成模型，合成最终视频内容。',
    collect_results: '汇总所有生成结果，整理输出为最终交付物。',
    start: '初始化流水线，准备执行环境和必要资源。',
  }
  if (descriptions[step]) return descriptions[step]
  if (step.startsWith('generate_')) {
    return '根据生图方案调用生图模型生成指定商品图片。'
  }
  return ''
}

/** Group pipeline history events by step_name. */
function groupHistoryByStep(history: MonitorPipelineDetail['history']): Map<string, MonitorPipelineDetail['history']> {
  const map = new Map<string, MonitorPipelineDetail['history']>()
  for (const h of history) {
    const stepName = h.step_name || ''
    if (!stepName) continue
    if (!map.has(stepName)) map.set(stepName, [])
    map.get(stepName)!.push(h)
  }
  return map
}

/** Extract LLM diagnostics from a group of step events. */
function getStepLlmDiag(events: MonitorPipelineDetail['history']): {
  model: Record<string, unknown> | null
  params: Record<string, unknown> | null
  response: Record<string, unknown> | null
} {
  for (const h of events) {
    const d = h.event_data || {}
    const model = d.llm_model && typeof d.llm_model === 'object'
      ? (d.llm_model as Record<string, unknown>)
      : null
    const params = d.llm_params && typeof d.llm_params === 'object'
      ? (d.llm_params as Record<string, unknown>)
      : null
    const response = d.llm_response && typeof d.llm_response === 'object'
      ? (d.llm_response as Record<string, unknown>)
      : null
    if (model || params || response) return { model, params, response }
  }
  return { model: null, params: null, response: null }
}

/** Calculate duration from first event → last event within a group of step events. */
function getStepDuration(events: MonitorPipelineDetail['history']): string | null {
  if (events.length < 2) return null
  const first = new Date(events[0].created_at).getTime()
  const last = new Date(events[events.length - 1].created_at).getTime()
  const diff = last - first
  if (diff < 0) return null
  const mins = Math.floor(diff / 60000)
  const secs = Math.floor((diff % 60000) / 1000)
  if (mins > 0) return `${mins}分${secs}秒`
  return `${secs}秒`
}

// ── Pool bar component ────────────────────────────────────────────────

function PoolBar({ label, pool, icon: Icon }: { label: string; pool: PoolInfo; icon: React.ComponentType<{ className?: string }> }) {
  // Tenant-local view: no global capacity denominator — just show whether the
  // tenant has active items in this pool (the bar is a presence indicator).
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="text-[11px] w-10 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            pool.active > 0 ? 'bg-primary' : 'bg-muted-foreground/20',
          )}
          style={{ width: pool.active > 0 ? '100%' : '0%' }}
        />
      </div>
      <span className="text-[11px] tabular-nums w-12 text-right shrink-0 text-muted-foreground">
        {pool.active}
      </span>
    </div>
  )
}

// ── Floating Button (Draggable) ────────────────────────────────────────

const DRAG_THRESHOLD = 5 // px — minimum distance to qualify as a drag vs click

export function TaskMonitorFloatingButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  // 1-second tick so elapsed time updates in real-time
  const [tick, setTick] = useState(0)
  // "Show all" toggles for the list tabs (cap at LIST_DISPLAY_LIMIT by default)
  const [showAllPipelines, setShowAllPipelines] = useState(false)
  const [showAllTasks, setShowAllTasks] = useState(false)
  const [showAllCommands, setShowAllCommands] = useState(false)
  const { t } = useI18n()

  // ── Drag state ─────────────────────────────────────────────────
  const btnRef = useRef<HTMLButtonElement>(null)
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    // Restore saved position, or default to bottom-right
    if (typeof window === 'undefined') return { x: 0, y: 0 }
    const saved = localStorage.getItem('taskMonitorPosition')
    if (saved) {
      try { return JSON.parse(saved) }
      catch { /* ignore */ }
    }
    // Default to bottom-right
    return { x: window.innerWidth - 80, y: Math.max(100, window.innerHeight - 180) }
  })
  const dragState = useRef<{ startX: number; startY: number; moved: boolean } | null>(null)
  const positionRef = useRef(position)

  // Keep the ref in sync with state
  positionRef.current = position

  // Save position to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('taskMonitorPosition', JSON.stringify(position))
  }, [position])

  const handlePointerDown = (e: React.PointerEvent) => {
    // Only primary button
    if (e.button !== 0) return
    const btn = btnRef.current
    if (!btn) return
    btn.setPointerCapture(e.pointerId)
    dragState.current = {
      startX: e.clientX - positionRef.current.x,
      startY: e.clientY - positionRef.current.y,
      moved: false,
    }
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    const ds = dragState.current
    if (!ds) return
    const newX = e.clientX - ds.startX
    const newY = e.clientY - ds.startY
    const dx = newX - positionRef.current.x
    const dy = newY - positionRef.current.y
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      ds.moved = true
    }
    if (ds.moved) {
      // Clamp to viewport bounds (button is ~48px wide/tall)
      const maxX = window.innerWidth - 48
      const maxY = window.innerHeight - 48
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      })
    }
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    const ds = dragState.current
    if (!ds) return
    const btn = btnRef.current
    if (btn) btn.releasePointerCapture(e.pointerId)
    if (!ds.moved) {
      // It was a click — toggle the panel
      setIsOpen((v) => !v)
    }
    dragState.current = null
  }

  // ONE shared hook — single source of truth
  const { data, isLoading, refetch } = useTaskSystem()

  // Force re-render every second while the panel is open so elapsed time stays live
  useEffect(() => {
    if (!isOpen) return
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [isOpen])

  const pipelines = data?.pipelines?.items ?? []
  const creationTasks = data?.creation_tasks?.items ?? []
  const activePipelines = pipelines.filter(p => ACTIVE_PIPELINE_STATUSES.includes(p.status))
  const activeCreationTasks = creationTasks.filter(t => ACTIVE_TASK_STATUSES.includes(t.status))

  // ── Actions ────────────────────────────────────────────────

  const handleCancelPipeline = async (id: string) => {
    try { await pipelineApi.cancelPipeline(id); toast.success('已取消流水线'); refetch() }
    catch { toast.error('取消失败') }
  }

  const handleDeletePipeline = async (id: string) => {
    try { await pipelineApi.deletePipeline(id); toast.success('已删除流水线'); refetch() }
    catch { toast.error('删除失败') }
  }

  const handleRetryPipeline = async (id: string) => {
    try { await pipelineApi.retryPipeline(id); toast.success('已重试流水线'); refetch() }
    catch { toast.error('重试失败') }
  }

  const handleCancelTask = async (id: string) => {
    try { await creationApi.cancelTask(id); toast.success('已取消任务'); refetch() }
    catch { toast.error('取消失败') }
  }

  const handleDeleteTask = async (id: string) => {
    try { await creationApi.deleteTask(id); toast.success('已删除任务'); refetch() }
    catch { toast.error('删除失败') }
  }

  const handleRetryTask = async (id: string) => {
    try { await creationApi.retryTask(id); toast.success('已重试任务'); refetch() }
    catch { toast.error('重试失败') }
  }

  const handleCancelCommand = async (id: string) => {
    try { await commandApi.cancelJob(id); toast.success('已取消命令'); refetch() }
    catch { toast.error('取消失败') }
  }

  // ── Badge count ────────────────────────────────────────────

  const activeCount = activePipelines.length + activeCreationTasks.length + (data?.queue.by_status?.running || 0)

  return (
    <>
      <button
        ref={btnRef}
        data-task-monitor
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={cn(
          'fixed z-40 flex items-center justify-center rounded-full shadow-lg select-none touch-none',
          'transition-all duration-300 ease-out',
          'active:scale-95',
          isOpen
            ? 'bg-primary/90 text-primary-foreground shadow-xl shadow-primary/20 scale-105'
            : 'bg-white/80 dark:bg-white/10 backdrop-blur-xl border border-white/40 dark:border-white/20 hover:shadow-xl hover:shadow-primary/10 hover:border-white/60 dark:hover:border-white/30',
          dragState.current?.moved ? 'cursor-grabbing' : 'cursor-grab',
          'group',
        )}
        style={{
          left: position.x,
          top: position.y,
          width: 52,
          height: 52,
        }}
      >
        {isOpen ? (
          <div className="relative flex items-center justify-center">
            <X className="h-5 w-5" />
          </div>
        ) : (
          <>
            {/* Pulsing ring when tasks are active */}
            {activeCount > 0 && (
              <span className="absolute inset-0 rounded-full animate-ping opacity-30 bg-primary/40" />
            )}

            {/* Inner content */}
            <div className="relative flex items-center justify-center">
              {/* Outer ring glow */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary/10 via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Icon */}
              <div className="relative">
                <Activity className="h-5 w-5 text-primary/80 group-hover:text-primary transition-colors duration-300" />
                {/* Subtle dot pulse */}
                <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
                  {activeCount > 0 ? (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  ) : null}
                  <span className={cn(
                    'relative inline-flex rounded-full h-1.5 w-1.5',
                    activeCount > 0 ? 'bg-primary' : 'bg-muted-foreground/30',
                  )} />
                </span>
              </div>
            </div>

            {/* Badge */}
            {activeCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white shadow-lg shadow-red-500/30">
                {activeCount > 99 ? '99+' : activeCount}
              </span>
            )}
          </>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setIsOpen(false)} />
          {(() => {
            const panelWidth = 480
            const gap = 12
            const btnSize = 52
            const viewW = window.innerWidth
            const viewH = window.innerHeight
            const panelMaxH = Math.min(viewH * 0.7, viewH - 48)

            // Horizontal: align so the panel's right edge aligns with the button's
            // right edge (panel extends leftward). This way the button is at the
            // edge of the panel rather than underneath it.
            const btnRight = position.x + btnSize
            const panelLeft = Math.max(8, Math.min(btnRight - panelWidth, viewW - panelWidth - 8))

            // Vertical: ensure the panel NEVER overlaps the button.
            const spaceAbove = Math.max(0, position.y - 8)
            const spaceBelow = Math.max(0, viewH - position.y - btnSize - 8)

            let panelTop: number
            let panelMaxHeight: number

            if (spaceAbove >= panelMaxH + gap) {
              // Above: plenty of room
              panelTop = position.y - gap - panelMaxH
              panelMaxHeight = panelMaxH
            } else if (spaceBelow >= panelMaxH + gap) {
              // Below: plenty of room
              panelTop = position.y + btnSize + gap
              panelMaxHeight = panelMaxH
            } else if (spaceAbove >= spaceBelow) {
              // Above: limited room — cap height so panel fits above the button
              panelMaxHeight = Math.max(100, Math.min(panelMaxH, spaceAbove - gap))
              panelTop = position.y - gap - panelMaxHeight
            } else {
              // Below: limited room — cap height so panel fits below the button
              panelMaxHeight = Math.max(100, Math.min(panelMaxH, spaceBelow - gap))
              panelTop = position.y + btnSize + gap
            }

            // ScrollArea viewport needs to account for header (~48px) + tabs (~40px) + padding (~32px)
            const scrollAreaMaxH = panelMaxHeight - 120

            return (
              <div data-task-monitor className={cn(
                'fixed z-50 w-[480px] max-w-[calc(100vw-3rem)] rounded-xl border bg-background/80 backdrop-blur-xl shadow-2xl overflow-hidden',
                'flex flex-col',
              )}
                style={{ left: panelLeft, top: panelTop, maxHeight: panelMaxHeight }}
              >
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0 bg-gradient-to-r from-primary/[0.03] to-transparent">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center h-6 w-6 rounded-md bg-primary/10">
                  <Activity className="h-3.5 w-3.5 text-primary" />
                </div>
                <h2 className="text-sm font-semibold">任务监控</h2>
                {data && (
                  <span className="text-[11px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded-full">
                    {data.queue.worker.active} 运行中
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => refetch()} title="刷新">
                  <Loader2 className={cn('h-3 w-3', isLoading && 'animate-spin')} />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsOpen(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Loading state */}
            {isLoading && !data && (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">加载任务状态...</p>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && data && pipelines.length === 0 && creationTasks.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
                <Activity className="h-8 w-8 opacity-30" />
                <p className="text-xs">暂无任务</p>
              </div>
            )}

            {/* Content */}
            {data && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                <div className="px-4 pt-2 shrink-0">
                  <TabsList className="w-full h-8">
                    <TabsTrigger value="overview" className="text-xs h-7 gap-1">
                      <BarChart3 className="h-3 w-3" />概览
                    </TabsTrigger>
                    <TabsTrigger value="pipelines" className="text-xs h-7 gap-1 relative">
                      <Layers className="h-3 w-3" />流水线
                      {activePipelines.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activePipelines.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="text-xs h-7 gap-1 relative">
                      <Box className="h-3 w-3" />生图任务
                      {activeCreationTasks.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activeCreationTasks.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="commands" className="text-xs h-7 gap-1">
                      <Terminal className="h-3 w-3" />队列
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Panel is max-h (height:auto), so flex-1 + min-h-0 alone don't bound the
                    viewport height. Cap the viewport directly so it overflows and shows the
                    Radix scrollbar; also make the thumb more visible.
                    Radix wraps scroll content in <div style="min-width:100%; display:table">;
                    plain min-w-0/max-w-full can't override those inline styles, and display:table
                    lets a long unbreakable title stretch the whole region. Force it to block
                    layout + fixed width with !important. */}
                <ScrollArea className="min-h-0 flex-1 min-w-0 px-4 py-3 overflow-hidden [&>[data-slot=scroll-area-viewport]]:max-w-full [&>[data-slot=scroll-area-viewport]]:max-h-[var(--scroll-max-h)] [&>[data-slot=scroll-area-viewport]]:overflow-x-hidden [&>[data-slot=scroll-area-viewport]>div]:!block [&>[data-slot=scroll-area-viewport]>div]:!w-full [&>[data-slot=scroll-area-viewport]>div]:!min-w-0 [&>[data-slot=scroll-area-viewport]>div]:max-w-full [&>[data-slot=scroll-area-viewport]>div]:overflow-hidden [&_[data-slot=scroll-area-thumb]]:bg-muted-foreground/40"
    style={{ '--scroll-max-h': `${scrollAreaMaxH}px` } as React.CSSProperties}>
                  <TabsContent value="overview" className="mt-0 w-full min-w-0 max-w-full space-y-4">
                    {/* Pool concurrency bars */}
                    {data.queue.worker.pools && (
                      <div className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-muted-foreground">本租户运行中</span>
                          <span className="text-[11px] text-muted-foreground">
                            排队命令: {data.queue.by_status?.new || 0}
                          </span>
                        </div>
                        <PoolBar label="流水线" pool={data.queue.worker.pools.pipeline} icon={GitBranch} />
                        <PoolBar label="生图" pool={data.queue.worker.pools.creation} icon={Image} />
                        <PoolBar label="其他" pool={data.queue.worker.pools.other} icon={Cpu} />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <SummaryCard label="流水线" active={activePipelines.length} total={pipelines.length} icon={Layers} />
                      <SummaryCard label="生图任务" active={activeCreationTasks.length} total={creationTasks.length} icon={Box} />
                      <SummaryCard label="队列命令" active={data.queue.by_status?.running || 0} total={data.queue.total} icon={ListTodo} />
                      <SummaryCard label="已完成" active={0} total={data.queue.by_status?.completed || 0} icon={CheckCircle2} color="text-emerald-500" />
                    </div>

                    {activePipelines.length > 0 && (
                      <>
                        <h3 className="text-xs font-medium text-muted-foreground">运行中的流水线</h3>
                        <div className="space-y-1.5">
                          {activePipelines.slice(0, 5).map((p) => (
                            <MiniTaskRow
                              key={p.id}
                              title={p.title || pipelineTypeLabel(p.pipeline_type)}
                              status={p.status}
                              progress={p.progress}
                              time={elapsedTimeLive(p.started_at)}
                              onClick={() => setActiveTab('pipelines')}
                              stepStartedAt={p.step_started_at}
                              progressConfig={p.progress_config}
                              currentStep={p.current_step ? getStepLabel(p.current_step, t) : undefined}
                              totalSteps={p.total_steps}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {activeCreationTasks.length > 0 && (
                      <>
                        <h3 className="text-xs font-medium text-muted-foreground">运行中的生图任务</h3>
                        <div className="space-y-1.5">
                          {activeCreationTasks.slice(0, 5).map((t) => (
                            <MiniTaskRow
                              key={t.id}
                              title={t.title || t.creation_type}
                              status={t.status}
                              progress={t.progress}
                              subtitle={`${t.model_name || t.provider}`}
                              time={elapsedTimeLive(t.started_at)}
                              onClick={() => setActiveTab('tasks')}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="pipelines" className="mt-0 w-full min-w-0 max-w-full space-y-2">
                    {pipelines.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                        <Layers className="h-8 w-8 opacity-30" /><p className="text-xs">暂无流水线</p>
                      </div>
                    )}
                    {pipelines.slice(0, showAllPipelines ? pipelines.length : LIST_DISPLAY_LIMIT).map((p) => (
                      <TaskRow
                        key={p.id}
                        t={t}
                        title={p.title || pipelineTypeLabel(p.pipeline_type)}
                        status={p.status}
                        progress={p.progress}
                        subtitle={pipelineTypeLabel(p.pipeline_type)}
                        time={formatTime(p.created_at)}
                        error={p.error_message}
                        duration={['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(p.status)
                          ? formatDuration(p.started_at, p.updated_at)
                          : elapsedTimeLive(p.started_at)}
                        totalSteps={p.total_steps}
                        currentStep={p.current_step ? getStepLabel(p.current_step, t) : undefined}
                        stepStartedAt={p.step_started_at}
                        progressConfig={p.progress_config}
                        fetchDetail={() => taskMonitorApi.getPipelineDetail(p.id)}
                        onCancel={p.status === 'pending' || p.status === 'running' ? () => handleCancelPipeline(p.id) : undefined}
                        onDelete={() => handleDeletePipeline(p.id)}
                        onRetry={p.status === 'failed' || p.status === 'canceled' || p.status === 'partially_succeeded' ? () => handleRetryPipeline(p.id) : undefined}
                      />
                    ))}
                    {pipelines.length > LIST_DISPLAY_LIMIT && (
                      <ListToggle total={pipelines.length} expanded={showAllPipelines} onToggle={() => setShowAllPipelines(v => !v)} />
                    )}
                  </TabsContent>

                  <TabsContent value="tasks" className="mt-0 w-full min-w-0 max-w-full space-y-2">
                    {creationTasks.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                        <Box className="h-8 w-8 opacity-30" /><p className="text-xs">暂无生图任务</p>
                      </div>
                    )}
                    {creationTasks.slice(0, showAllTasks ? creationTasks.length : LIST_DISPLAY_LIMIT).map((task) => (
                      <TaskRow
                        key={task.id}
                        t={t}
                        title={task.title || task.creation_type}
                        status={task.status}
                        progress={task.progress}
                        subtitle={`${task.model_name || task.provider} · ${task.module_type}`}
                        time={formatTime(task.created_at)}
                        error={task.error_message}
                        duration={['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(task.status)
                          ? formatDuration(task.started_at, task.updated_at)
                          : elapsedTimeLive(task.started_at)}
                        progressLabel={`${task.succeeded_output_count || 0}/${task.expected_output_count || 1}`}
                        onCancel={task.status === 'pending' || task.status === 'queued' || task.status === 'running' ? () => handleCancelTask(task.id) : undefined}
                        onRetry={task.status === 'failed' || task.status === 'canceled' ? () => handleRetryTask(task.id) : undefined}
                        onDelete={() => handleDeleteTask(task.id)}
                      />
                    ))}
                    {creationTasks.length > LIST_DISPLAY_LIMIT && (
                      <ListToggle total={creationTasks.length} expanded={showAllTasks} onToggle={() => setShowAllTasks(v => !v)} />
                    )}
                  </TabsContent>

                  <TabsContent value="commands" className="mt-0 w-full min-w-0 max-w-full space-y-2">
                    {data.recent_commands && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {Object.entries(data.queue.by_status).map(([s, count]) => (
                          <Badge key={s} variant={statusBadgeVariant(s)} className="flex items-center gap-1">
                            {statusIcon(s)}{statusLabel(s)}<span className="font-bold">{count}</span>
                          </Badge>
                        ))}
                      </div>
                    )}
                    {(!data.recent_commands || data.recent_commands.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                        <Terminal className="h-8 w-8 opacity-30" /><p className="text-xs">暂无命令记录</p>
                      </div>
                    )}
                    {data.recent_commands?.slice(0, showAllCommands ? data.recent_commands.length : LIST_DISPLAY_LIMIT).map((cmd) => (
                      <TaskRow
                        key={cmd.id}
                        t={t}
                        title={cmd.name}
                        status={cmd.status}
                        progress={cmd.attempts > 0 ? Math.min(cmd.attempts * 20, 100) : undefined}
                        subtitle={cmd.app}
                        time={formatTime(cmd.created_at)}
                        error={cmd.error_message}
                        duration={['succeeded', 'completed', 'failed', 'canceled'].includes(cmd.status)
                          ? formatDuration(cmd.started_at, cmd.completed_at ?? cmd.updated_at)
                          : elapsedTimeLive(cmd.started_at)}
                        onAction={cmd.status === 'running' ? () => handleCancelCommand(cmd.id) : undefined}
                      />
                    ))}
                    {data.recent_commands && data.recent_commands.length > LIST_DISPLAY_LIMIT && (
                      <ListToggle total={data.recent_commands.length} expanded={showAllCommands} onToggle={() => setShowAllCommands(v => !v)} />
                    )}
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            )}
          </div>
            )
          })()}
        </>
      )}
    </>
  )
}

// ── Sub-components ────────────────────────────────────────────────────

/** "Show all / collapse" toggle rendered under a truncated list. */
function ListToggle({ total, expanded, onToggle }: {
  total: number
  expanded: boolean
  onToggle: () => void
}) {
  if (total <= LIST_DISPLAY_LIMIT) return null
  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-full h-7 text-[11px] text-muted-foreground"
      onClick={onToggle}
    >
      {expanded ? '收起' : `显示全部 ${total} 条`}
    </Button>
  )
}

function SummaryCard({ label, active, total, icon: Icon, color }: {
  label: string; active: number; total: number; icon: React.ComponentType<{ className?: string }>; color?: string
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center justify-between mb-1">
        <Icon className={cn('h-3.5 w-3.5', color || 'text-muted-foreground')} />
        {active > 0 && (
          <span className="text-[10px] font-bold text-amber-500 bg-amber-50 dark:bg-amber-950/30 px-1.5 py-0.5 rounded">{active}</span>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-bold">{total}</span>
        <span className="text-[11px] text-muted-foreground">{label}</span>
      </div>
    </div>
  )
}

function MiniTaskRow({ title, status, progress, time, subtitle, onClick, stepStartedAt, progressConfig, currentStep, totalSteps }: {
  title: string; status: string; progress?: number; time: string; subtitle?: string; onClick?: () => void
  stepStartedAt?: string | null; progressConfig?: PipelineProgressStage[]
  currentStep?: string | null; totalSteps?: number | null
}) {
  const isActive = ['running', 'pending', 'queued', 'new'].includes(status)
  const isDone = ['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(status)
  const displayProgress = useFakeProgress(progress ?? 0, isActive, isDone, stepStartedAt, progressConfig)

  return (
    <button onClick={onClick} className="w-full min-w-0 max-w-full flex items-center gap-2 overflow-hidden rounded-lg border px-3 py-2 hover:bg-accent/50 transition-colors text-left">
      <div className={cn('shrink-0', statusColor(status))}>{statusIcon(status)}</div>
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium truncate flex-1 min-w-0">{title}</span>
          {subtitle && <span className="text-[10px] text-muted-foreground truncate min-w-0">{subtitle}</span>}
        </div>
        {currentStep && isActive && (
          <div className="flex items-center gap-1 mt-0.5 min-w-0">
            <span className="text-[10px] text-blue-500 truncate flex-1 min-w-0">{currentStep}</span>
            {totalSteps && <span className="text-[10px] text-muted-foreground shrink-0">/ {totalSteps}步</span>}
          </div>
        )}
        {progress !== undefined && progress > 0 && (
          <div className="h-1 rounded-full bg-muted mt-1 overflow-hidden max-w-[120px]">
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(displayProgress, 100)}%` }} />
          </div>
        )}
      </div>
      <span className="text-[10px] text-muted-foreground shrink-0">{time}</span>
      <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
    </button>
  )
}

// ── Pipeline detail helpers ───────────────────────────────────────────

/** Extract the error string from a history event's event_data, if any. */
function getEventError(h: MonitorPipelineDetail['history'][number]): string | null {
  const d = h.event_data || {}
  const err = d.error ?? d.error_message
  if (typeof err === 'string' && err.trim()) return err.trim()
  if (d.status === 'failed') return '任务失败'
  return null
}

/** Extract LLM diagnostics (model + request params + response) from pipeline history events.
 *  Returns the first event that carries llm_model / llm_params / llm_response (the analyze_product step). */
function extractLlmDiagnostics(history: MonitorPipelineDetail['history']): {
  model: Record<string, unknown> | null
  params: Record<string, unknown> | null
  response: Record<string, unknown> | null
} {
  for (const h of history) {
    const d = h.event_data || {}
    const model = d.llm_model && typeof d.llm_model === 'object'
      ? (d.llm_model as Record<string, unknown>)
      : null
    const params = d.llm_params && typeof d.llm_params === 'object'
      ? (d.llm_params as Record<string, unknown>)
      : null
    const response = d.llm_response && typeof d.llm_response === 'object'
      ? (d.llm_response as Record<string, unknown>)
      : null
    if (model || params || response) return { model, params, response }
  }
  return { model: null, params: null, response: null }
}

/** Build a display-friendly ordered list of LLM call params (tool_choice dedup when == tool). */
function buildLlmParamItems(params: Record<string, unknown>): [string, string][] {
  const order = ['temperature', 'top_p', 'tool', 'tool_choice', 'image_count', 'prompt_len']
  const items: [string, string][] = []
  for (const k of order) {
    if (params[k] === undefined || params[k] === null) continue
    if (k === 'tool_choice' && params[k] === params['tool']) continue
    items.push([k, String(params[k])])
  }
  return items
}

/** Build displayable reference-image items from pipeline input_assets.
 *  缩略图统一走 cdnImg + zipImg(w_200)：解析 tencent:///aliyun:// 存储前缀为 CDN URL，
 *  并对项目 CDN（tcimg001/img001）追加缩放/转 webp 参数，避免对话框加载原图。 */
function buildReferenceImageItems(assets: Array<Record<string, unknown>>): { url: string; role: string }[] {
  const items: { url: string; role: string }[] = []
  for (const a of assets ?? []) {
    if (a.media_type === 'image') {
      const url = zipImg(cdnImg(String(a.thumbnail_url || a.file_url || '')))
      if (url) items.push({ url, role: String(a.asset_role || 'image') })
    }
  }
  return items
}

/** Human-readable label for a pipeline history event. */
function historyEventLabel(h: MonitorPipelineDetail['history'][number], t: (key: string, vars?: Record<string, string | number>) => string): string {
  const step = h.step_name ? getStepLabel(h.step_name, t) : ''
  switch (h.event_type) {
    case 'created': return '已创建'
    case 'started': return '开始执行'
    case 'step_done': {
      const d = h.event_data || {}
      const isFail = d.error || d.status === 'failed'
      return step ? `${isFail ? '步骤失败' : '步骤完成'}: ${step}` : (isFail ? '步骤失败' : '步骤完成')
    }
    case 'completed': return '流水线完成'
    case 'failed': return '流水线失败'
    case 'cancelled': return '已取消'
    default: return step ? `${h.event_type}: ${step}` : h.event_type
  }
}

function TaskRow({ title, status, progress, subtitle, time, error, duration, progressLabel, totalSteps, currentStep, stepStartedAt, progressConfig, fetchDetail, onCancel, onDelete, onRetry, onAction, t }: {
  title: string; status: string; progress?: number; subtitle?: string; time: string; error?: string | null; duration?: string
  progressLabel?: string; totalSteps?: number | null; currentStep?: string | null; stepStartedAt?: string | null; progressConfig?: PipelineProgressStage[]
  fetchDetail?: () => Promise<MonitorPipelineDetail>
  onCancel?: () => void; onDelete?: () => void; onRetry?: () => void; onAction?: () => void
  t: (key: string, vars?: Record<string, string | number>) => string
}) {
  const [expanded, setExpanded] = useState(false)
  const [detail, setDetail] = useState<MonitorPipelineDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const isActive = ['running', 'pending', 'queued', 'new'].includes(status)
  const isDone = ['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(status)
  const displayProgress = useFakeProgress(progress ?? 0, isActive, isDone, stepStartedAt, progressConfig)
  // 从 pipeline 详情 history 中提取最近一次 LLM 调用使用的模型与请求参数
  const llmDiag = detail ? extractLlmDiagnostics(detail.history) : { model: null, params: null, response: null }
  // 参考图/商品图 —— 任务监控里展示本次流水线使用的输入图片
  const referenceImages = detail ? buildReferenceImageItems(detail.input_assets ?? []) : []

  // Fetch detail once when the row is first expanded
  useEffect(() => {
    if (!expanded || !fetchDetail || detail || detailLoading) return
    setDetailLoading(true)
    setDetailError(null)
    fetchDetail()
      .then(setDetail)
      .catch(() => setDetailError('加载详情失败'))
      .finally(() => setDetailLoading(false))
  }, [expanded, fetchDetail, detail, detailLoading])

  return (
    <div className="rounded-lg border overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full min-w-0 max-w-full flex items-center gap-2 overflow-hidden px-3 py-2.5 hover:bg-accent/50 transition-colors text-left">
        <div className={cn('shrink-0', statusColor(status))}>{statusIcon(status)}</div>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-medium truncate flex-1 min-w-0">{title}</span>
            <Badge variant={statusBadgeVariant(status)} className="h-5 px-1.5 text-[10px] shrink-0">{statusLabel(status)}</Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            {subtitle && <span className="text-[10px] text-muted-foreground truncate flex-1 min-w-0">{subtitle}</span>}
            {duration && duration !== '—' && <span className="text-[10px] text-muted-foreground shrink-0">{duration}</span>}
            {progressLabel && <span className="text-[10px] text-muted-foreground shrink-0">输出: {progressLabel}</span>}
          </div>
          {currentStep && isActive && (
            <div className="flex items-center gap-1 mt-0.5 min-w-0">
              <span className="text-[10px] text-blue-500 font-medium truncate flex-1 min-w-0">当前: {currentStep}</span>
              {totalSteps && <span className="text-[10px] text-muted-foreground shrink-0">/ 共{totalSteps}步</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {progress !== undefined && progress > 0 && <span className="text-[11px] font-medium tabular-nums text-muted-foreground min-w-[32px] text-center">{displayProgress}%</span>}
          <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')} />
        </div>
      </button>

      {progress !== undefined && progress > 0 && (
        <div className="h-1 bg-muted">
          <div className={cn('h-full', status === 'failed' ? 'bg-red-500' : 'bg-primary')} style={{ width: `${Math.min(displayProgress, 100)}%` }} />
        </div>
      )}

      {expanded && (
        <div className="border-t px-3 py-2 space-y-2 bg-muted/30">
          {currentStep && (
            <div className="flex min-w-0 items-center justify-between gap-2 text-[11px]">
              <span className="text-muted-foreground shrink-0">当前步骤</span>
              <span className="font-medium truncate min-w-0">{currentStep}</span>
            </div>
          )}
          {totalSteps && (
            <div className="flex min-w-0 items-center justify-between gap-2 text-[11px]">
              <span className="text-muted-foreground shrink-0">总步骤</span>
              <span className="font-medium truncate min-w-0">{totalSteps}</span>
            </div>
          )}
          <div className="flex min-w-0 items-center justify-between gap-2 text-[11px]">
            <span className="text-muted-foreground shrink-0">创建时间</span>
            <span className="font-medium truncate min-w-0">{time}</span>
          </div>
          {duration && duration !== '—' && (
            <div className="flex min-w-0 items-center justify-between gap-2 text-[11px]">
              <span className="text-muted-foreground shrink-0">耗时</span>
              <span className="font-medium truncate min-w-0">{duration}</span>
            </div>
          )}

          {llmDiag.model && (
            <div className="space-y-1.5 rounded-md border bg-background p-2">
              <div className="flex min-w-0 items-center justify-between gap-2 text-[11px]">
                <span className="text-muted-foreground shrink-0">模型</span>
                <span
                  className="font-medium truncate min-w-0 text-right"
                  title={llmDiag.model.base_url ? String(llmDiag.model.base_url) : undefined}
                >
                  {String(llmDiag.model.model ?? '未知')}
                  {Boolean(llmDiag.model.provider) && (
                    <span className="font-normal text-muted-foreground"> · {String(llmDiag.model.provider)}</span>
                  )}
                </span>
              </div>
              {llmDiag.params && (
                <div className="space-y-1">
                  <span className="block text-[11px] text-muted-foreground">调用参数</span>
                  <div className="flex flex-wrap gap-1">
                    {buildLlmParamItems(llmDiag.params).map(([k, v]) => (
                      <span key={k} className="rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-foreground/80">
                        {k}={v}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {llmDiag.response && (
                <div className="space-y-1.5 border-t pt-1.5">
                  <span className="block text-[11px] text-muted-foreground">返回值</span>
                  {(llmDiag.response.finish_reason !== undefined && llmDiag.response.finish_reason !== null || typeof llmDiag.response.has_tool_calls === 'boolean') && (
                    <div className="flex flex-wrap gap-1">
                      {llmDiag.response.finish_reason !== undefined && llmDiag.response.finish_reason !== null && (
                        <span className="rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-foreground/80">
                          finish_reason={String(llmDiag.response.finish_reason)}
                        </span>
                      )}
                      {typeof llmDiag.response.has_tool_calls === 'boolean' && (
                        <span className="rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-foreground/80">
                          has_tool_calls={String(llmDiag.response.has_tool_calls)}
                        </span>
                      )}
                    </div>
                  )}
                  {Array.isArray(llmDiag.response.tool_calls) && llmDiag.response.tool_calls.length > 0 && (
                    <div className="space-y-1">
                      {llmDiag.response.tool_calls.map((tc, i) => (
                        <div key={i} className="rounded bg-muted/60 p-1 font-mono text-[10px] text-foreground/80 break-words">
                          <span className="font-semibold">{String(tc.name ?? '?')}</span>
                          {typeof tc.args === 'string' && tc.args && (
                            <pre className="mt-0.5 whitespace-pre-wrap break-words font-mono text-[10px] text-foreground/70">{sanitizeForDisplay(tc.args.slice(0, 300)) as string}</pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {typeof llmDiag.response.content === 'string' && llmDiag.response.content && (
                    <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded bg-muted/60 p-1.5 font-mono text-[10px] text-foreground/80">
                      {sanitizeForDisplay(llmDiag.response.content) as string}
                    </pre>
                  )}
                  {typeof llmDiag.response.raw_preview === 'string' && llmDiag.response.raw_preview && (
                    <div className="space-y-0.5">
                      <span className="block text-[10px] text-muted-foreground">原始响应 (raw)</span>
                      <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded bg-muted/60 p-1.5 font-mono text-[10px] text-foreground/70">
                        {sanitizeForDisplay(llmDiag.response.raw_preview) as string}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {referenceImages.length > 0 && (
            <div className="space-y-1.5 rounded-md border bg-background p-2">
              <span className="block text-[11px] text-muted-foreground">参考图 ({referenceImages.length})</span>
              <div className="flex flex-wrap gap-1.5">
                {referenceImages.map((img, i) => (
                  <img
                    key={`${img.url}-${i}`}
                    src={img.url}
                    alt={img.role}
                    className="h-12 w-12 rounded-md object-cover bg-muted ring-1 ring-black/5"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden' }}
                  />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded bg-red-50 dark:bg-red-950/20 p-2 text-[11px] text-red-600 dark:text-red-400 break-words whitespace-pre-wrap">{error}</div>
          )}

          {/* ── Pipeline detail (child tasks + history) ── */}
          {fetchDetail && detailLoading && (
            <div className="flex items-center gap-2 py-2 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> 加载详情...
            </div>
          )}
          {fetchDetail && detailError && (
            <div className="rounded bg-red-50 dark:bg-red-950/20 p-2 text-[11px] text-red-600 dark:text-red-400 break-words">{detailError}</div>
          )}
          {detail && (
            <>
              {detail.error_message && (
                <div className="rounded bg-red-50 dark:bg-red-950/20 p-2 text-[11px] text-red-600 dark:text-red-400 break-words whitespace-pre-wrap">
                  {detail.error_code && <span className="font-mono text-[10px] mr-1 opacity-70">[{detail.error_code}]</span>}
                  {detail.error_message}
                </div>
              )}
              {/* ── Step accordion: group history + child_tasks by step ── */}
              {detail.history && detail.history.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-muted-foreground">执行记录（按步骤）</p>
                  <StepAccordion
                    detail={detail}
                    t={t}
                  />
                </div>
              )}
            </>
          )}

          <div className="flex items-center gap-1 pt-1 border-t">
            {isActive && onCancel && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={(e) => { e.stopPropagation(); onCancel() }}>
                <Square className="h-3 w-3" />取消
              </Button>
            )}
            {onRetry && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1"
                onClick={(e) => { e.stopPropagation(); onRetry() }}>
                <RotateCcw className="h-3 w-3" />重试
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); onDelete() }}>
                <Trash2 className="h-3 w-3" />删除
              </Button>
            )}
            {onAction && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1"
                onClick={(e) => { e.stopPropagation(); onAction() }}>
                <Square className="h-3 w-3" />取消
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Step Accordion (execution record grouped by step) ──────────────────

function StepAccordion({ detail, t }: {
  detail: MonitorPipelineDetail
  t: (key: string, vars?: Record<string, string | number>) => string
}) {
  const stepGroups = groupHistoryByStep(detail.history)
  const stepOrder = ['start', 'analyze_product', 'vision', 'brief', 'detail_composer', 'video_composer', 'generate_images', 'generate_video', 'collect_results']

  // Build step list preserving order, with any unknown steps appended
  const knownSteps = stepOrder.filter(s => stepGroups.has(s))
  const unknownSteps = Array.from(stepGroups.keys()).filter(s => !stepOrder.includes(s))
  const allSteps = [...knownSteps, ...unknownSteps]

  // Build a map of child_tasks by step for quick lookup
  const childTasksByStep = new Map<string, MonitorPipelineChildTask>()
  if (detail.child_tasks) {
    for (const ct of detail.child_tasks) {
      if (ct.step) childTasksByStep.set(ct.step, ct)
    }
  }

  return (
    <div className="space-y-1.5">
      {allSteps.map((stepName, idx) => {
        const events = stepGroups.get(stepName)!
        const childTask = childTasksByStep.get(stepName)
        const llmDiag = getStepLlmDiag(events)
        const duration = getStepDuration(events)
        const stepError = events.find(h => getEventError(h))
        const hasError = !!stepError
        const stepLabel = getStepLabel(stepName, t)
        const stepDescription = getStepDescription(stepName)

        return (
          <StepAccordionItem
            key={stepName}
            stepIndex={idx + 1}
            stepName={stepName}
            stepLabel={stepLabel}
            stepDescription={stepDescription}
            events={events}
            llmDiag={llmDiag}
            duration={duration}
            hasError={hasError}
            childTask={childTask}
            t={t}
          />
        )
      })}
    </div>
  )
}

function StepAccordionItem({ stepIndex, stepName, stepLabel, stepDescription, events, llmDiag, duration, hasError, childTask, t }: {
  stepIndex: number
  stepName: string
  stepLabel: string
  stepDescription: string
  events: MonitorPipelineDetail['history']
  llmDiag: { model: Record<string, unknown> | null; params: Record<string, unknown> | null; response: Record<string, unknown> | null }
  duration: string | null
  hasError: boolean
  childTask: MonitorPipelineChildTask | undefined
  t: (key: string, vars?: Record<string, string | number>) => string
}) {
  const [open, setOpen] = useState(false)

  // Find first event for timing
  const firstEvent = events[0]

  return (
    <div className={cn('rounded-md border overflow-hidden', hasError && 'border-red-200 dark:border-red-900')}>
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-2 px-2.5 py-2 text-left transition-colors hover:bg-accent/50',
          open && 'border-b bg-muted/40',
        )}
      >
        <ChevronRight className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
        <span className="text-[11px] font-medium text-muted-foreground shrink-0">步骤 {stepIndex}</span>
        <span className="text-[11px] font-semibold truncate flex-1 min-w-0">{stepLabel}</span>
        {childTask && (
          <span className={cn('shrink-0', statusColor(childTask.status))}>
            {statusIcon(childTask.status)}
          </span>
        )}
        {duration && (
          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{duration}</span>
        )}
        {hasError && (
          <AlertCircle className="h-3 w-3 shrink-0 text-red-500" />
        )}
      </button>

      {/* Body — dump ALL raw data */}
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-background text-[11px]">
          {/* 步骤说明 */}
          {stepDescription && (
            <p className="text-foreground/80 leading-relaxed">{stepDescription}</p>
          )}

          {/* 时间信息 */}
          {firstEvent && (
            <div className="flex items-center gap-4 text-muted-foreground">
              <span>开始时间：{new Date(firstEvent.created_at).toLocaleString('zh-CN')}</span>
              {duration && <span>运行时长：{duration}</span>}
            </div>
          )}

          {/* 模型信息 */}
          {llmDiag.model && (() => {
            const m = llmDiag.model as Record<string, unknown>
            return (
              <div className="rounded border bg-muted/30 p-1.5">
                <div className="font-medium text-muted-foreground mb-1">模型</div>
                <pre className="whitespace-pre-wrap break-words font-mono text-[10px] text-foreground/80">
                  {JSON.stringify(m, null, 2)}
                </pre>
              </div>
            )
          })()}

          {/* 参数 */}
          {llmDiag.params && (() => {
            const p = sanitizeForDisplay(llmDiag.params) as Record<string, unknown>
            return (
              <div className="rounded border bg-muted/30 p-1.5">
                <div className="font-medium text-muted-foreground mb-1">参数</div>
                <pre className="whitespace-pre-wrap break-words font-mono text-[10px] text-foreground/80">
                  {JSON.stringify(p, null, 2)}
                </pre>
              </div>
            )
          })()}

          {/* 返回值 */}
          {llmDiag.response && (() => {
            const r = sanitizeForDisplay(llmDiag.response) as Record<string, unknown>
            return (
              <div className="rounded border bg-muted/30 p-1.5">
                <div className="font-medium text-muted-foreground mb-1">返回值</div>
                <pre className="whitespace-pre-wrap break-words font-mono text-[10px] text-foreground/80 max-h-60 overflow-y-auto">
                  {JSON.stringify(r, null, 2)}
                </pre>
              </div>
            )
          })()}

          {/* 每条事件的原始 event_data */}
          <div className="space-y-1">
            <div className="font-medium text-muted-foreground">详细日志</div>
            {events.map((h) => {
              const err = getEventError(h)
              const hasActualData = h.event_data && Object.keys(h.event_data).length > 0 && JSON.stringify(h.event_data) !== '{}'
              return (
                <div key={h.id} className="rounded border bg-muted/20 p-1.5 space-y-1">
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span className={err ? 'text-red-500' : 'text-emerald-500'}>
                      {err ? '✗' : '✓'}
                    </span>
                    <span className="tabular-nums">{new Date(h.created_at).toLocaleString('zh-CN')}</span>
                    <span className="font-medium">{h.event_type}</span>
                    {h.step_name && <span className="text-muted-foreground/60">({h.step_name})</span>}
                  </div>
                  {hasActualData && (
                    <pre className="whitespace-pre-wrap break-words font-mono text-[10px] text-foreground/70 max-h-40 overflow-y-auto bg-background/50 rounded p-1">
                      {JSON.stringify(sanitizeForDisplay(h.event_data), null, 2)}
                    </pre>
                  )}
                  {err && (
                    <div className="text-[10px] text-red-600 dark:text-red-400 break-words whitespace-pre-wrap">{err}</div>
                  )}
                </div>
              )
            })}
          </div>

          {/* 子任务错误 */}
          {childTask?.error && (
            <div className="rounded bg-red-50 dark:bg-red-950/20 p-1.5 text-[10px] text-red-600 dark:text-red-400 break-words whitespace-pre-wrap">
              {childTask.error}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

